import { Check, ChevronDown, ChevronRight, Copy, ExternalLink } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { EvaluationsPanel } from '@/components/organisms/EvaluationsPanel';
import { ThreadSidebar } from '@/components/organisms/ThreadSidebar';
import { hasContent, Transcript } from '@/components/organisms/Transcript';
import { copyToClipboard } from '@/utils/clipboard';

import { INTERCOM_CHANNEL, intercomConversationUrl } from './ConversationDetail.constants';
import { ConversationDetailLoading } from './ConversationDetail.loading';
import { useConversation, useConversationEvents } from './ConversationDetail.requests';
import { styles } from './ConversationDetail.styles';

export const ConversationDetail = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const { threadId } = useParams();
	const { data: conv, isLoading, isError } = useConversation(threadId ?? '');
	// Trace interno (agent_events + variantes). No bloquea el render: sin
	// eventos la página se comporta exactamente como antes.
	const { data: trace } = useConversationEvents(threadId ?? '');
	// `?trace=<id>` — the operator arrives with an id from Grafana and lands on
	// the turn it belongs to.
	const [searchParams] = useSearchParams();
	const highlightTraceId = searchParams.get('trace');
	// The latest turn's trace is the one offered for copying: it is almost
	// always the one being looked at when something went wrong.
	const latestTraceId = trace?.events?.filter((e) => e.traceId).at(-1)?.traceId ?? null;
	const evaluationsRef = useRef<HTMLDivElement>(null);

	if (isLoading) return <ConversationDetailLoading />;
	if (isError || !conv) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	const visibleMessages = conv.messages.filter(hasContent);

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<Link to="/conversations" className="hover:text-default">
						{t('BREADCRUMB_PARENT')}
					</Link>
					<ChevronRight className="h-sm w-sm" />
					<span className="font-mono font-medium text-default">{conv.threadId}</span>
				</nav>
				<div className="flex items-center gap-xs">
					<CopyIdButton threadId={conv.threadId} />
					{conv.channel.toLowerCase() === INTERCOM_CHANNEL && (
						<a
							href={intercomConversationUrl(conv.threadId)}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-3xs rounded-md border border-subtle bg-white px-xs py-3xs text-[11px] text-default transition-colors hover:bg-grey-25"
						>
							<ExternalLink className="h-sm w-sm" />
							{t('OPEN_INTERCOM')}
						</a>
					)}
				</div>
			</header>

			<main className={styles.shell}>
				<div className={styles.column}>
					<header className="flex items-start gap-xs">
						<div className={styles.userHeaderAvatar}>{conv.userInitials}</div>
						<div className="min-w-0">
							<p className="mb-2xs flex items-center gap-2xs text-[11px] font-medium uppercase tracking-wider text-purple-700">
								{t('EYEBROW')}
								<code className="font-mono text-[11px] normal-case tracking-normal text-subtle">{conv.threadId}</code>
							</p>
							<h1 className={styles.pageH1}>{conv.userName}</h1>
							<p className="mt-2xs text-[13px] text-subtle">
								{conv.companyName} · {conv.country}
							</p>
						</div>
					</header>

					<Transcript messages={visibleMessages} events={trace?.events ?? []} highlightTraceId={highlightTraceId} />

					<div ref={evaluationsRef}>
						<EvaluationsPanel threadId={conv.threadId} />
					</div>

					<RawStateBlock rawState={conv.rawState} />
				</div>

				<ThreadSidebar
					conversation={conv}
					turns={visibleMessages.length}
					variants={trace?.variants ?? {}}
					traceId={highlightTraceId ?? latestTraceId}
					onEvaluationsClick={() => evaluationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
				/>
			</main>
		</>
	);
};

const CopyIdButton = ({ threadId }: { threadId: string }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const [copied, setCopied] = useState(false);

	const handleClick = async () => {
		if (!(await copyToClipboard(threadId))) return;
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1500);
	};

	return (
		<button
			type="button"
			onClick={handleClick}
			aria-live="polite"
			className={`inline-flex items-center gap-3xs text-[11px] transition-colors ${
				copied ? 'text-success' : 'text-subtle hover:text-default'
			}`}
		>
			{copied ? <Check className="h-sm w-sm" /> : <Copy className="h-sm w-sm" />}
			{copied ? t('COPIED') : t('COPY_ID')}
		</button>
	);
};

const RawStateBlock = ({ rawState }: { rawState: Record<string, unknown> }) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const [open, setOpen] = useState(false);

	return (
		<section className="border-t border-subtle pt-md">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="group flex w-full items-baseline justify-between text-left"
			>
				<div>
					<h2 className={`${styles.sectionH} transition-colors group-hover:text-subtle`}>{t('RAW_STATE_TITLE')}</h2>
					<p className={styles.sectionSub}>{t('RAW_STATE_SUB')}</p>
				</div>
				<ChevronDown className={`h-[14px] w-[14px] text-subtle transition-transform ${open ? 'rotate-180' : ''}`} />
			</button>
			{open && (
				<pre className="mt-md overflow-x-auto rounded-md border border-subtle bg-grey-25 px-xs py-xs text-[11px] text-default">
					{JSON.stringify(rawState, null, 2)}
				</pre>
			)}
		</section>
	);
};
